import clsx from "clsx";
import { ReactNode, useCallback } from "react";
import Box from "./Box";

interface Props {
  title: string;
  leftLabel?: string | (() => ReactNode);
  rightLabel?: string;
  className?: string;
  values?: {
    label: string;
    value?: string;
    unit: string;
    description?: string;
    className?: string;
  }[];
  children?: ReactNode;
}

const Board = ({
  title,
  leftLabel,
  rightLabel,
  className,
  values,
  children,
}: Props) => {
  const renderLeftLabel = useCallback(() => {
    if (!leftLabel) {
      return null;
    }
    if (typeof leftLabel === "string") {
      return (
        <span className="text-12 text-secondary relative inline-block -bottom-[0.125rem]">
          {leftLabel}
        </span>
      );
    }
    return leftLabel();
  }, [leftLabel]);

  return (
    <Box className={clsx("!pb-0", className)}>
      <div className="flex justify-between pb-[0.625rem] gap-5 border-b border-gray">
        <div className="flex gap-[0.625rem]">
          <h3 className="font-medium text-18 shrink-0">{title}</h3>
          {renderLeftLabel()}
        </div>
        {rightLabel && (
          <span className="text-14 text-secondary shrink-0">{rightLabel}</span>
        )}
      </div>
      {values && (
        <ul className="flex pt-[1.625rem] pb-[2rem] -mx-[1.875rem]">
          {values.map(
            ({ label, value, description, unit, className }, index) => (
              <li
                key={label}
                className={clsx(
                  "relative flex-1 flex flex-col items-center gap-[1.938rem] pt-[0.438rem] pb-[1.563rem]",
                  {
                    "after:absolute after:h-full after:w-px after:right-0 after:bg-gray":
                      index !== values.length - 1,
                  },
                )}
              >
                <span className="text-14 text-secondary">
                  {label || "\u00A0"}
                </span>
                <div className="flex flex-col">
                  <span className="text-16 font-medium text-secondary">
                    <strong className={clsx("text-36 font-bold", className)}>
                      {value || "\u00A0"}
                    </strong>{" "}
                    {value ? unit : ""}
                  </span>
                  {description && (
                    <span className="absolute bottom-0 text-14 text-secondary">
                      {description}
                    </span>
                  )}
                </div>
              </li>
            ),
          )}
        </ul>
      )}
      {children}
    </Box>
  );
};

export default Board;
